<template>
	<div class="manage-space px-12 pt-4">
		<NavBar :admin="true" :items="items" />
		<v-main class="mb-12">
			<v-card class="mb-2">
				<v-card-title class="primary--text">
					<translate>Space</translate>
				</v-card-title>
				<v-card-text>
					<v-simple-table>
						<template v-slot:default>
							<thead>
								<tr>
									<th class="text-left">Nome</th>
									<th class="text-left">Nome da Empresa</th>
									<th class="text-left">NIF</th>
									<th class="text-left">Telemóvel</th>
									<th class="text-left">Nº Profissionais</th>
									<th class="text-left">Nº Pacientes</th>
									<th class="text-left">Nº Consultas</th>
									<th class="text-left" v-if="canRemoveSpace">Remover</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td>{{ space.space_name }}</td>
									<td>{{ space.company_name }}</td>
									<td>{{ space.company_nif }}</td>
									<td>{{ space.phone }}</td>
									<td>{{ users.length }}</td>
									<td>{{ num_patients }}</td>
									<td>{{ num_appointments }}</td>
									<td v-if="canRemoveSpace">
										<v-btn small color="primary" @click="showRemoveSpace = true"><translate>Remove</translate></v-btn>
										<v-dialog v-model="showRemoveSpace" max-width="500">
											<v-card>
												<v-card-title class="title primary--text mb-2 secondary">Remove Space</v-card-title>
												<v-card-text>This operation is irreversible! All data related to this space will be deleted. Are you sure you want to delete it?</v-card-text>
												<v-divider></v-divider>
												<v-card-actions>
													<v-btn color="primary darken-1" text @click="showRemoveSpace = false"><translate>Cancel</translate></v-btn>
													<v-spacer></v-spacer>
													<v-btn
														color="primary darken-1"
														text
														@click="deleteSpace"
													><translate>Remove</translate></v-btn>
												</v-card-actions>
											</v-card>
										</v-dialog>
									</td>
								</tr>
							</tbody>
						</template>
					</v-simple-table>
				</v-card-text>
			</v-card>
			<v-card v-if="student" class="mb-2">
				<v-card-title class="primary--text">
					<translate>Student</translate>
				</v-card-title>
				<v-card-text>
					<v-simple-table>
						<template v-slot:default>
							<thead>
								<tr>
									<th class="text-left" v-translate>School</th>
									<th class="text-left" v-translate>Student Number</th>
									<th class="text-left" v-translate>Proof of enrollment</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td>{{ student.school }}</td>
									<td>{{ student.student_number }}</td>
									<td>
										<v-btn v-if="student.student_proof" @click="openFile(student.student_proof)" small color="primary">Download</v-btn>
										<span v-else>Não inserido.</span>
									</td>
								</tr>
							</tbody>
						</template>
					</v-simple-table>
				</v-card-text>
			</v-card>
			<v-card class="mb-2">
				<v-card-title class="primary--text">
					<translate>Professionals</translate>
				</v-card-title>
				<v-card-text>
					<v-simple-table>
						<template v-slot:default>
							<thead>
								<tr>
									<th class="text-left" v-translate>Name</th>
									<th class="text-left" v-translate>Role</th>
									<th class="text-left" v-translate>Email</th>
									<th class="text-left" v-translate>Creation Date</th>
									<th class="text-left" v-translate>Last login</th>
									<th class="text-left" v-translate>Browser</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="user in users" :key="user.id">
									<td>{{ user.name }}</td>
									<td>{{ user.role }}</td>
									<td>{{ user.email }}</td>
									<td>{{ user.created }}</td>
									<td>{{ user.last_login }}</td>
									<td>{{ user.browser }}</td>
								</tr>
							</tbody>
						</template>
					</v-simple-table>
				</v-card-text>
			</v-card>
			<v-card class="mb-2">
				<v-card-title class="primary--text">
					<translate>Current Plan</translate>
				</v-card-title>
				<v-card-text>
					<v-row no-gutters>
						<v-col cols="6">
							<v-card>
								<v-card-title v-if="subscriptionPlan">
									{{subscriptionPlan.name}}<span v-if="subscriptionPlan.custom" class="ml-1">(Alterado)</span>
									<v-btn icon x-small class="ml-1" color="primary" @click="showEditDays = false; showEditEndDate = false; showEditPlan = !showEditPlan">
										<v-icon small>mdi-circle-edit-outline</v-icon>
									</v-btn>
								</v-card-title>
								<v-card-title v-else>
									Sem Plano
								</v-card-title>
								<v-card-text v-if="showEditPlan">
									<v-form ref="planForm" v-model="validForm">
										<v-select
											v-model="newSubscriptionPlan"
											:items="subscriptionPlans"
											:label="$gettext('Plan')"
											item-text="name"
											item-value="id"
											outlined
											class="px-2"
											:rules="[required]"
											return-object
										></v-select>
										<template v-if="newSubscriptionPlan">
											<v-row no-gutters>
												<v-col cols="6">
													<v-text-field
														v-model="newSubscriptionPlan.num_professionals"
														:label="$gettext('Professionals')"
														outlined
														class="px-2"
													/>
												</v-col>
												<v-col cols="6">
													<v-text-field
														v-model="newSubscriptionPlan.num_secretaries"
														:label="$gettext('Secretaries')"
														outlined
														class="px-2"
													/>
												</v-col>
												<v-col cols="6">
													<v-text-field
														v-model="newSubscriptionPlan.num_patients"
														:label="$gettext('Patients')"
														outlined
														class="px-2"
													/>
												</v-col>
												<v-col cols="6">
													<v-text-field
														v-model="newSubscriptionPlan.disk_space"
														:label="$gettext('Disk space')"
														outlined
														class="px-2"
													/>
												</v-col>
											</v-row>
										</template>
									</v-form>
								</v-card-text>
								<v-card-text v-else-if="subscriptionPlan">
									<template v-if="subscriptionPlan.custom">
										<span class="mr-1"><translate>Patients</translate>:</span>{{subscriptionPlan.num_patients}}<br>
										<span class="mr-1"><translate>Professionals</translate>:</span>{{subscriptionPlan.num_professionals}}<br>
										<span class="mr-1"><translate>Receptionists</translate>:</span>{{subscriptionPlan.num_secretaries}}<br>
										<span class="mr-1"><translate>Disk space</translate>:</span>{{subscriptionPlan.disk_space}}<br>
									</template>
									<!-- <template v-if="!subscriptionPlan.start_date">
										<p class="red--text font-weight-bold mb-1">Expirado</p>
										<span class="mr-1"><translate>Expiration Date</translate>:</span>{{subscriptionPlan.end_date}}<br>
									</template> -->
									<template>
										<p v-if="!subscriptionPlan.start_date" class="red--text font-weight-bold mb-1">Expirado</p>
										<span key="p-date" class="mr-1"><translate>Purchase Date</translate>:</span>{{subscriptionPlan.start_date}}<br>
										<span key="e-date" class="mr-1"><translate>Expiration Date</translate>:</span>{{subscriptionPlan.end_date}}
										<v-btn icon x-small color="primary" @click="showEditDays = false; showEditSMS=false; showEditEndDate = !showEditEndDate">
											<v-icon small>mdi-circle-edit-outline</v-icon>
										</v-btn><br>
										<span key="days" class="mr-1"><translate>Days</translate>:</span>{{subscriptionPlan.days_left}}
										<v-btn icon x-small color="primary" @click="showEditEndDate = false; showEditSMS=false; showEditDays = !showEditDays">
											<v-icon small>mdi-circle-edit-outline</v-icon>
										</v-btn><br>
										<span key="sms" class="mr-1">SMS:</span>{{space.sms_available}}
										<v-btn icon x-small color="primary" @click="showEditDays = false; showEditEndDate = false; showEditSMS = !showEditSMS">
											<v-icon small>mdi-circle-edit-outline</v-icon>
										</v-btn><br>
									</template>
									<template v-if="showEditDays">
										<v-row no-gutters align="center" class="mt-3">
											<v-col>
												<v-text-field
													label="Dias"
													v-model="days"
													outlined
													hide-details
													type="number"
													dense
												/>
											</v-col>
											<v-col>
												<v-btn
													small
													color="primary"
													class="ml-2"
													@click="extendPlan"
												>Extender</v-btn>
											</v-col>
											<v-col>
												<v-row no-gutters>
													<v-spacer></v-spacer>
													<v-btn small color="primary" @click="expirePlan">Expirar Plano</v-btn>
												</v-row>
											</v-col>
										</v-row>
									</template>
									<template v-if="showEditEndDate">
										<v-row no-gutters align="center" class="mt-3">
											<v-col>
												<v-menu
													v-model="dateMenu"
													:close-on-content-click="false"
													:nudge-right="40"
													transition="scale-transition"
													offset-y
													min-width="290px"
												>
													<template v-slot:activator="{ on }">
														<v-text-field
															v-model="subscriptionPlan.end_date"
															dense
															:label="$gettext('Date')"
															append-icon="event"
															readonly
															v-on="on"
															outlined
															:rules="[required]"
															hide-details
														></v-text-field>
													</template>
													<v-date-picker color="primary" v-model="subscriptionPlan.end_date" @input="dateMenu = false"></v-date-picker>
												</v-menu>
											</v-col>
											<v-col>
												<v-btn small color="primary" class="ml-2" @click="savePlanEndDate">Guardar</v-btn>
											</v-col>
										</v-row>
									</template>
									<template v-if="showEditSMS">
										<v-row no-gutters align="center" class="mt-3">
											<v-col>
												<v-text-field
													label="SMS"
													v-model="sms"
													outlined
													hide-details
													type="number"
													dense
												/>
											</v-col>
											<v-col>
												<v-btn
													small
													color="primary"
													class="ml-2"
													@click="addSMS"
												>Adicionar</v-btn>
											</v-col>
											<v-col>
												<v-row no-gutters>
													<v-spacer></v-spacer>
													<v-btn small color="primary" @click="expirePlan">Expirar Plano</v-btn>
												</v-row>
											</v-col>
										</v-row>
									</template>
								</v-card-text>
								<v-card-actions v-if="showEditPlan">
									<v-spacer></v-spacer>
									<!-- <v-btn v-if="!showEditPlan" text small color="primary" @click="showEditPlan = true" key="edit"><translate>Edit</translate></v-btn> -->
									<v-btn text small color="primary" @click="showEditPlan = false" key="cancel"><translate>Cancel</translate></v-btn>
									<v-btn text small color="primary" @click="savePlan" key="save"><translate>Save</translate></v-btn>
								</v-card-actions>
							</v-card>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
			<v-card class="mb-2">
				<v-card-title class="primary--text">
					<translate>Payments</translate>
				</v-card-title>
				<v-card-text>
					<p v-if="payments.length == 0" class="font-weight-thin ml-2" v-translate key="no-payments">No payments.</p>
					<v-list v-else>
						<PaymentItem v-for="(payment, index) in payments" :key="index" :payment="payment" :admin="true" />
					</v-list>
				</v-card-text>
			</v-card>
			<v-card class="mb-2">
				<v-card-title class="primary--text">
					<translate>Owned Modules</translate>
				</v-card-title>
				<v-card-text>
					<p v-if="ownModules.length == 0" class="font-weight-thin ml-2" v-translate key="no-own-modules">No modules.</p>
					<v-simple-table v-else>
						<template v-slot:default>
							<thead>
								<tr>
									<th class="text-left" v-translate>Module</th>
									<th class="text-left" v-translate>Remove Module</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="module in ownModules" :key="module.id">
									<td>{{ module.name }}</td>
									<td>
										<v-btn small class="mb-3" color="primary" @click="removeModule(module.id)"><translate>Remove</translate></v-btn>
									</td>
								</tr>
							</tbody>
						</template>
					</v-simple-table>
				</v-card-text>
			</v-card>
			<v-card class="mb-2">
				<v-card-title class="primary--text">
					<translate>Other Modules</translate>
				</v-card-title>
				<v-card-text>
					<p v-if="otherModules.length == 0" class="font-weight-thin ml-2" v-translate key="no-other-modules">No more modules.</p>
					<v-simple-table v-else>
						<template v-slot:default>
							<thead>
								<tr>
									<th class="text-left">Módulo</th>
									<th class="text-left">Ativar</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="module in otherModules" :key="module.id">
									<td>{{ module.name }}</td>
									<td style="width: 200px">
										<v-btn
											small
											class="mb-3"
											color="primary"
											@click="activateModule(module.id)"
										>Activate</v-btn>
									</td>
								</tr>
							</tbody>
						</template>
					</v-simple-table>
				</v-card-text>
			</v-card>
		</v-main>
	</div>
</template>

<script>
import NavBar from "@/components/authentication/NavBar.vue";
import PaymentItem from "@/components/payments/PaymentItem.vue";
import axios from "axios";

export default {
	name: "ManageSpace",
	props: ["sid"],
	components: {
		NavBar,
		PaymentItem
	},
	data: () => ({
		space: {},
		showRemoveSpace: false,
		users: [],
		ownModules: [],
		otherModules: [],
		num_appointments: 0,
		num_patients: 0,
		user_id: null,
		subscriptionPlan: {},
		showEditPlan: false,
		subscriptionPlans: [],
		payments: [],
		newSubscriptionPlan: null,
		validForm: false,
		days: null,
		sms: null,
		showEditDays: false,
		showEditEndDate: false,
		showEditSMS: false,
		dateMenu: false
	}),
	computed: {
		items() {
			return [
				{
					text: this.$gettext("Dashboard"),
					disabled: false,
					link: true,
					to: "/admin/dashboard"
				},
				{
					text: this.$gettext("Spaces"),
					disabled: false,
					link: true,
					to: { name: "spaces" }
				},
				{
					text: this.space.space_name,
					disabled: true,
					link: true
				}
			];
		},
		required() {
			return value => !!value || this.$gettext("Required.");
    },
		canRemoveSpace() {
			if(this.space.id == 7) return false;
			if(this.subscriptionPlan && this.subscriptionPlan.start_date) return false;
			return true;
		},
		student() {
			for(let i=0; i < this.users.length; i++) {
				if(this.users[i].role == 'manager' && this.users[i].student) return this.users[i];
			}
			return null;
		}
	},
	methods: {
		deleteSpace() {
			axios
				.post("/admin/spaces/" + this.space.id + "/remove")
				.then(response => {
					console.log(response.data);
					if(response.data.result == 'ok'){
						this.showRemoveSpace = false;
						this.$router.push({name: "spaces"})
					}
				})
				.catch(error => console.log(error));
		},
		activateModule(id) {
			axios
				.post("/admin/spaces/" + this.space.id + "/module/" + id + "/activate")
				.then(response => {
					console.log(response.data);
					this.ownModules = response.data.ownModules;
					this.otherModules = response.data.otherModules;
				})
				.catch(error => console.log(error));
		},
		removeModule(id) {
			axios
				.post("/admin/spaces/" + this.space.id + "/module/" + id + "/remove")
				.then(response => {
					console.log(response.data);
					this.ownModules = response.data.ownModules;
					this.otherModules = response.data.otherModules;
				})
				.catch(error => console.log(error));
		},
		extendPlan() {
			if(this.days) { 
				const formData = new FormData();
				formData.append("days", this.days);
				axios
					.post(
						"/admin/spaces/" + this.space.id + "/extend-plan",
						formData
					)
					.then(response => {
						console.log(response.data);
						this.days = null;
						this.subscriptionPlan = response.data.subscription_plan;
						this.showEditDays = false;
					})
					.catch(error => console.log(error));
			}
		},
		addSMS() {
			if(this.sms) { 
				const formData = new FormData();
				formData.append("sms", this.sms);
				axios
					.post(
						"/admin/spaces/" + this.space.id + "/add-sms",
						formData
					)
					.then(response => {
						console.log(response.data);
						this.sms = null;
						this.space.sms_available = response.data.sms_available;
						this.showEditSMS = false;
					})
					.catch(error => console.log(error));
			}
		},
		expirePlan() {
			axios
				.post(
					"/admin/spaces/" + this.space.id + "/expire-plan")
					.then(response => {
						console.log(response.data);
						this.subscriptionPlan = response.data.subscription_plan;
						this.showEditDays = false;
					})
					.catch(error => console.log(error));
		},
		savePlan() {
			if(this.$refs.planForm.validate()) {
				axios
					.post("/admin/spaces/" + this.space.id + "/save-plan", this.newSubscriptionPlan)
					.then(response => {
						console.log(response.data);
						this.showEditPlan = false;
						this.newSubscriptionPlan = null;
						this.subscriptionPlan = response.data;
					})
					.catch(error => console.log(error));
			}
		},
		savePlanEndDate() {
			if(this.subscriptionPlan.end_date) { 
				const formData = new FormData();
				formData.append("date", this.subscriptionPlan.end_date);
				axios
					.post(
						"/admin/spaces/" + this.space.id + "/save-plan-end-date",
						formData
					)
					.then(response => {
						console.log(response.data);
						this.subscriptionPlan = response.data.subscription_plan;
						this.showEditEndDate = false;
					})
					.catch(error => console.log(error));
			}
		},
		openFile(code) {
			axios
				.get("/download/" + code)
				.then(response => {
					if (response.status === 200) {
						var link = document.createElement("a");
            link.href = "https://api.naltio.com/download/" + code;
            //link.href = "http://127.0.0.1:5500/download/" + code;
						link.target = "_self";
						document.body.appendChild(link);
						link.click();
					}
				})
				.catch(error => {
					console.log(error);
				});
    },
	},
	created() {
		axios
			.get("/admin/spaces/" + this.sid + "/manage")
			.then(response => {
				console.log(response.data);
				this.space = response.data.space;
				this.ownModules = response.data.own_modules;
				this.otherModules = response.data.other_modules;
				this.users = response.data.users;
				this.num_appointments = response.data.num_appointments;
				this.num_patients = response.data.num_patients;
				this.subscriptionPlan = response.data.subscription_plan;
				this.subscriptionPlans = response.data.subscription_plans;
				this.payments = response.data.payments;
			})
			.catch(error => console.log(error));
	}
};
</script>